import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { markMessageAsRead } from '@/backend/messageServiceApi'
import {
  MESSAGE_CHANNELS_QUERY_KEY,
  MESSAGES_LIST_QUERY_KEY,
  MESSAGES_UNREAD_QUERY_KEY,
} from '@/queryClient'
import {
  decrementChannelUnreadInList,
  decrementUnreadCount,
  markMessageReadInList,
  shouldMarkMessageAsRead,
  getMessageId,
} from '@/components/notifications/notificationLogic.mjs'

const inFlight = new Set()

/**
 * Пометка одного уведомления прочитанным по клику (оптимистично).
 */
export function useMarkMessageRead() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ id }) => markMessageAsRead(id),
    onMutate: async ({ id, channelId }) => {
      await queryClient.cancelQueries({ queryKey: [MESSAGES_LIST_QUERY_KEY] })
      await queryClient.cancelQueries({ queryKey: [MESSAGE_CHANNELS_QUERY_KEY] })
      const previousLists = queryClient.getQueriesData({ queryKey: [MESSAGES_LIST_QUERY_KEY] })
      const previousUnread = queryClient.getQueryData([MESSAGES_UNREAD_QUERY_KEY])
      const previousChannels = queryClient.getQueryData([MESSAGE_CHANNELS_QUERY_KEY])

      queryClient.setQueriesData({ queryKey: [MESSAGES_LIST_QUERY_KEY] }, (list) => {
        if (!Array.isArray(list)) return list
        return markMessageReadInList(list, id)
      })
      if (previousUnread != null) {
        queryClient.setQueryData([MESSAGES_UNREAD_QUERY_KEY], decrementUnreadCount(previousUnread))
      }
      if (channelId != null) {
        queryClient.setQueryData([MESSAGE_CHANNELS_QUERY_KEY], (list) => {
          if (!Array.isArray(list)) return list
          return decrementChannelUnreadInList(list, channelId)
        })
      }

      return { previousLists, previousUnread, previousChannels }
    },
    onError: (err, _vars, ctx) => {
      console.error('mark message read failed', err)
      ctx?.previousLists?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
      if (ctx && Object.prototype.hasOwnProperty.call(ctx, 'previousUnread')) {
        queryClient.setQueryData([MESSAGES_UNREAD_QUERY_KEY], ctx.previousUnread)
      }
      if (ctx && Object.prototype.hasOwnProperty.call(ctx, 'previousChannels')) {
        queryClient.setQueryData([MESSAGE_CHANNELS_QUERY_KEY], ctx.previousChannels)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [MESSAGES_UNREAD_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [MESSAGES_LIST_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [MESSAGE_CHANNELS_QUERY_KEY] })
    },
  })

  function markIfUnread(msg) {
    if (!shouldMarkMessageAsRead(msg)) return
    const id = getMessageId(msg)
    if (inFlight.has(id)) return
    inFlight.add(id)
    const channelId = Number(msg?.channel_id)
    mutation.mutate(
      {
        id,
        channelId: Number.isFinite(channelId) && channelId > 0 ? channelId : null,
      },
      {
        onSettled: () => { inFlight.delete(id) },
      },
    )
  }

  return { markIfUnread }
}
